import React from "react";
import { Box, Flex, IconButton, useTheme, Text } from "@chakra-ui/react";
import { MdOutlineModeEdit, MdOutlineDelete } from "react-icons/md";
import { Theme } from "../theme";
import Cookies from "js-cookie";
import getAxios from "../hooks/useAxios";
import axios from "axios";
import toAlphanumeric from "../utils/toAlphaNumeric";

export type SkaraImagePickerType = {
  url?: string;
  width?: string;
  height?: string;
  setUrl: (val: string) => void;
  handleDeleteButton?: Function;
  upperRange?: number;
  lowerRange?: number;
  requiredSize?: number;
  validationRequired?: boolean;
  setValidationError?: (val: boolean) => void;
  isDeleteRequired?: boolean;
};

const ImagePicker = ({
  url,
  handleDeleteButton,
  width,
  height,
  setUrl,
  lowerRange,
  upperRange,
  requiredSize,
  setValidationError,
  validationRequired = false,
  isDeleteRequired = true,
}: SkaraImagePickerType) => {
  const theme = useTheme<Theme>();
  const customAxios = getAxios();
  const token = Cookies.get("x-admin-auth");
  const [imageUrl, setImageUrl] = React.useState<string>();
  const [pickerId, setPickerId] = React.useState<string>();
  const [apiIsLoading, setApiIsLoading] = React.useState<string>();
  const [mouseOnCard, setMouseOnCard] = React.useState(false);
  const [mouseOnEdit, setMouseOnEdit] = React.useState({
    id: "",
    hovered: false,
  });

  const baseStyle = {
    width: width ? width : "150px",
    height: height ? height : "200px",
    justifyContent: "center",
    alignItems: "center",
    border: "1px solid",
    borderColor: theme.colors.secondary.main,
    borderRadius: "4px",
  };
  const baseButtonStyle = {
    background: "none",
    _hover: { background: theme.colors.primary.main },
    color: mouseOnCard ? "white" : theme.colors.primary.main,
  };
  const labelStyle = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "50%",
    cursor: "pointer",
  };

  React.useEffect(() => {
    const thumbnail_id =
      "thumbnail_" +
      Math.floor(Math.random() * 10000) +
      "_" +
      Math.floor(Math.random() * 10000000);
    setPickerId(thumbnail_id);
  }, []);

  React.useEffect(() => {
    if (url) {
      getImageLink(url).then((img) => {
        setImageUrl(img);
      });
    }
  }, [url]);

  function arrayBufferToBase64(buffer: any) {
    let binary = "";
    let bytes = new Uint8Array(buffer);
    let len = bytes.length;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  const getImageLink = async (url: string) => {
    try {
      const res = await customAxios.get(`uploads/${url}?token=${token}`, {
        responseType: "arraybuffer",
      });
      const buffer = res.data;
      let base64String = arrayBufferToBase64(buffer);
      let dataUri = "data:image/jpeg;base64," + base64String;
      return dataUri;
    } catch (error) {
      console.log("error", error);
    }
  };

  const uploadToS3 = async (file: File) => {
    const splitName = file.name.split(".")[0];
    const fileName = toAlphanumeric(splitName);
    console.log("file", file);
    const blob = new Blob([file], { type: file.type });
    try {
      const res = await customAxios.post("/uploads", { key: fileName });
      const key = res.data.key;
      const url = res.data.url;
      const res1 = await fetch(url, {
        method: "PUT",
        body: blob,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      console.log("res1", res1);
      setUrl(key);
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleImageApiCall = async (file: File) => {
    let formData = new FormData();
    formData.append("image", file);
    setApiIsLoading("loading");
    try {
      await uploadToS3(file);
      setApiIsLoading("success");
    } catch (error) {
      setApiIsLoading("error");
    }
  };

  const compressImage = (
    file: File,
    quality = 0.6,
    maxWidth = 1024
  ): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;

        img.onload = () => {
          let canvas = document.createElement("canvas");
          let scale = Math.min(maxWidth / img.width, 1);
          canvas.width = img.width * scale;
          canvas.height = img.height * scale;

          const ctx = canvas.getContext("2d");
          ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const originalFile = new File([blob], file.name, {
                  type: "image/jpeg",
                  lastModified: Date.now(),
                });
                resolve(originalFile);
              } else {
                reject(new Error("Compression failed"));
              }
            },
            "image/jpeg",
            quality
          );
        };
        img.onerror = (err) => reject(err);
      };
      reader.onerror = (err) => reject(err);
    });
  };

  const handleImageSelection = async (
    e: React.ChangeEvent<HTMLFormElement | HTMLInputElement>
  ) => {
    const chosenFiles = Array.prototype.slice.call(e.target.files);
    const originalFile = chosenFiles[0];
    try {
      //   const originalFile = await compressImage(originalFile, 0.6, 1024);
      const imgSize = originalFile?.size;
      console.log("imgSize", imgSize);

      const fileReader = new FileReader();
      let imgwidth, imgheight, ratio;
      fileReader.onload = () => {
        const img = new Image();
        img.onload = () => {
          imgwidth = img.width;
          imgheight = img.height;
          ratio = imgwidth / imgheight;
          if (validationRequired) {
            if (
              ratio >= (lowerRange as number) &&
              ratio <= (upperRange as number) &&
              imgSize < (requiredSize as number)
            ) {
              handleImageApiCall(originalFile);
              setValidationError && setValidationError(false);
            } else {
              setValidationError && setValidationError(true);
            }
          } else {
            handleImageApiCall(originalFile);
            setValidationError && setValidationError(false);
          }
        };
        img.src = fileReader.result as string;
      };
      if (originalFile) {
        fileReader.readAsDataURL(originalFile);
      }
    } catch (error) {
      console.error("Compression error:", error);
    }
  };

  if (apiIsLoading === "success") {
    return (
      <Flex
        id={pickerId}
        sx={{
          ...baseStyle,
          backgroundImage: imageUrl ? imageUrl : "",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
        }}
      >
        <Box>
          <Flex
            sx={{
              width: "100%",
              justifyContent: "space-around",
            }}
          >
            <label
              style={labelStyle}
              htmlFor={`upload_${pickerId}`}
              onMouseOver={() =>
                setMouseOnEdit({ id: pickerId as string, hovered: true })
              }
              onMouseOut={() =>
                setMouseOnEdit({ id: pickerId as string, hovered: false })
              }
            >
              <IconButton
                tabIndex={-1}
                sx={{
                  ...baseButtonStyle,
                  color: url
                    ? "white"
                    : mouseOnEdit.id === pickerId && mouseOnEdit.hovered
                    ? "white"
                    : theme.colors.primary.main,

                  backgroundColor:
                    mouseOnEdit.id === pickerId && mouseOnEdit.hovered
                      ? theme.colors.primary.main
                      : "none",
                }}
                aria-label="openSelectBox"
                size="md"
                icon={<MdOutlineModeEdit />}
                isRound={true}
                pointerEvents="none"
              />
            </label>
            {/* {id !== 0 && isDeleteRequired && (
                            <Box>
                                <IconButton
                                    tabIndex={-1}
                                    sx={{
                                        ...baseButtonStyle,
                                        color: theme.colors.bg.main,
                                    }}
                                    aria-label="deleteImage"
                                    size={"md"}
                                    icon={<MdOutlineDelete />}
                                    isRound={true}
                                    onClick={() => {
                                        handleDeleteButton && handleDeleteButton(id);
                                    }}
                                />
                            </Box>
                        )} */}
          </Flex>
          <input
            type="file"
            id={`upload_${pickerId}`}
            style={{ display: "none" }}
            accept="image/*"
            onChange={(e) => handleImageSelection(e)}
          />
        </Box>
      </Flex>
    );
  } else if (apiIsLoading === "loading") {
    return (
      <Flex id={pickerId} sx={baseStyle}>
        <Box>Loading...</Box>
      </Flex>
    );
  } else if (apiIsLoading === "error") {
    return (
      <Flex id={pickerId} sx={baseStyle}>
        <Box>
          <Flex
            sx={{
              width: "100%",
              justifyContent: "space-around",
            }}
          >
            <label style={labelStyle} htmlFor={`upload_${pickerId}`}>
              <IconButton
                tabIndex={-1}
                sx={baseButtonStyle}
                size={"md"}
                aria-label="openSelectBox"
                icon={<MdOutlineModeEdit />}
                isRound={true}
                pointerEvents={"none"}
              />
            </label>
            {isDeleteRequired && (
              <Box>
                <IconButton
                  tabIndex={-1}
                  sx={baseButtonStyle}
                  aria-label="deleteImage"
                  size={"md"}
                  icon={<MdOutlineDelete />}
                  isRound={true}
                  onClick={() => {
                    handleDeleteButton && handleDeleteButton();
                  }}
                />
              </Box>
            )}
          </Flex>
          <input
            type="file"
            name={pickerId}
            id={`upload_${pickerId}`}
            style={{ display: "none" }}
            accept="image/*"
            onChange={(e) => handleImageSelection(e)}
          />
        </Box>
      </Flex>
    );
  } else {
    return (
      <Flex
        id={pickerId}
        onMouseOver={() => setMouseOnCard(true)}
        onMouseOut={() => setMouseOnCard(false)}
        sx={{
          ...baseStyle,
          backgroundImage: imageUrl ? imageUrl : "none",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          _hover: {
            backgroundColor: "rgba(0,0,0,0.6)",
          },
        }}
      >
        <Box>
          <Flex
            sx={{
              width: "100%",
              justifyContent: "space-around",
              gap: "0.5rem",
            }}
          >
            <label
              style={labelStyle}
              htmlFor={`upload_${pickerId}`}
              onMouseOver={() =>
                setMouseOnEdit({ id: pickerId as string, hovered: true })
              }
              onMouseOut={() =>
                setMouseOnEdit({ id: pickerId as string, hovered: false })
              }
            >
              <IconButton
                tabIndex={-1}
                sx={{
                  ...baseButtonStyle,
                  color: url
                    ? "white"
                    : mouseOnEdit.id === pickerId && mouseOnEdit.hovered
                    ? "white"
                    : theme.colors.primary.main,

                  backgroundColor:
                    mouseOnEdit.id === pickerId && mouseOnEdit.hovered
                      ? theme.colors.primary.main
                      : "none",
                }}
                size={"md"}
                aria-label="openSelectBox"
                icon={<MdOutlineModeEdit />}
                isRound={true}
                pointerEvents={"none"}
              />
            </label>
            {/* {id !== 0 && isDeleteRequired && (
                            <Box>
                                <IconButton
                                    tabIndex={-1}
                                    sx={{
                                        ...baseButtonStyle,
                                        color: url ? "white" : theme.colors.primary.main,
                                        _hover: {
                                            color: "white",
                                            backgroundColor: theme.colors.primary.main,
                                        },
                                    }}
                                    aria-label="deleteImage"
                                    size={"md"}
                                    icon={<MdOutlineDelete />}
                                    isRound={true}
                                    onClick={() => {
                                        handleDeleteButton && handleDeleteButton(id);
                                    }}
                                />
                            </Box>
                        )} */}
            {/* {id === 0 &&
                            url?.slice(-1).split("").length !== 0 &&
                            isDeleteRequired && (
                                <Box>
                                    <IconButton
                                        tabIndex={-1}
                                        sx={{
                                            ...baseButtonStyle,
                                            color: url ? "white" : theme.colors.primary.main,
                                            _hover: {
                                                color: "white",
                                                backgroundColor: theme.colors.primary.main,
                                            },
                                        }}
                                        aria-label="deleteImage"
                                        size={"md"}
                                        icon={<MdOutlineDelete />}
                                        isRound={true}
                                        onClick={() => {
                                            handleDeleteButton && handleDeleteButton(id);
                                        }}
                                    />
                                </Box>
                            )} */}
          </Flex>
          <input
            type="file"
            id={`upload_${pickerId}`}
            style={{ display: "none" }}
            accept="image/*"
            onChange={(e) => handleImageSelection(e)}
          />
        </Box>
      </Flex>
    );
  }
};

export default ImagePicker;
